import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HOTKEYS, IHotkey, IOsSpecificKeyCombination } from '@app/shared/services/hotkeys/hotkeys';
import { UserService } from '@app/shared/user/state/user.service';

type IKeyMap = { [key in string]: IHotkey[] };

@Component({
    selector: 'studio-keymaps',
    templateUrl: './studio-keymaps.component.html',
    styleUrls: ['./studio-keymaps.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class StudioKeymapsComponent implements OnInit {
    private isMac = navigator.userAgent.includes('Mac');

    keymaps: IKeyMap = {};
    leftColumn = ['Design view', 'Toolbar', 'Edit elements'];
    rightColumn = ['Manage view', 'Timeline', 'Edit text', 'Translation Page'];

    constructor(
        private sanitizer: DomSanitizer,
        private userService: UserService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.setKeymaps();
    }

    private async setKeymaps(): Promise<void> {
        const isEmployee = await this.userService.isEmployee();
        this.keymaps = await HOTKEYS.filter(hk => hk.meta).reduce(
            async (accPromise, hotkey) => {
                return this.groupByCategory(await accPromise, hotkey, isEmployee);
            },
            Promise.resolve({} as IKeyMap)
        );
    }

    private async groupByCategory(
        acc: IKeyMap,
        hotkey: IHotkey,
        isEmployee: boolean
    ): Promise<IKeyMap> {
        const userHasFeaturePermissions =
            hotkey.permission && !(await this.userService.hasPermission(hotkey.permission));
        const employeeOnlyHotkey = hotkey.shouldBeEnabledForEmployeeOnly && !isEmployee;

        if (employeeOnlyHotkey || userHasFeaturePermissions) {
            return acc;
        }

        if (hotkey.meta !== undefined && hotkey.category) {
            if (
                typeof hotkey.meta.shortcut === 'object' &&
                hotkey.meta.shortcut.mac &&
                hotkey.meta.shortcut.win
            ) {
                hotkey.meta.shortcut = this.isMac ? hotkey.meta.shortcut.mac : hotkey.meta.shortcut.win;
            }

            if (typeof hotkey.category === 'string' && typeof hotkey.meta.shortcut !== 'object') {
                acc[hotkey.category] = (acc[hotkey.category] || []).concat(hotkey);
                return acc;
            } else if (
                typeof hotkey.category === 'object' &&
                typeof hotkey.meta.shortcut !== 'object'
            ) {
                hotkey.category!.forEach(category => {
                    acc[category] = (acc[category] || []).concat(hotkey);
                    return acc;
                });
            }
        }

        return acc;
    }

    separateByWord(value?: string | IOsSpecificKeyCombination): SafeHtml {
        if (typeof value !== 'string') {
            return this.sanitizer.bypassSecurityTrustHtml('');
        }

        const valueArr = value.split(' ');
        const regex = new RegExp(/(\b(or)\b)|\+|(\(Hold\)|(\(KeyUp\))|(\(DoubleTap\)))/, 'g');
        const regexOr = new RegExp(/(\b(or)\b)/, 'g');
        const regexClick = new RegExp(/(\b(click)\b)/, 'g');
        const regexScroll = new RegExp(/(\b(scroll)\b)/, 'g');
        const regexDrag = new RegExp(/(\b(drag)\b)/, 'g');
        const regexPlus = new RegExp(/(\b(Plus)\b)/, 'g');
        const regexMinus = new RegExp(/(\b(Minus)\b)/, 'g');
        const keymapsCommand = valueArr.reduce((acc: string[], item: string): string[] => {
            if (
                !regex.test(item) &&
                !regexClick.test(item) &&
                !regexScroll.test(item) &&
                !regexDrag.test(item)
            ) {
                let newItem;
                if (regexPlus.test(item)) {
                    newItem = item.replace(regexPlus, '+');
                } else if (regexMinus.test(item)) {
                    newItem = item.replace(regexMinus, '-');
                } else {
                    newItem = item;
                }
                acc.push(`<span class="keymap-value">${newItem}</span>`);
            } else if (regexOr.test(item)) {
                acc.push(`<span class="keymap-value-or">${item}</span>`);
            } else {
                acc.push(item);
            }
            return acc;
        }, []);
        return this.sanitizer.bypassSecurityTrustHtml(keymapsCommand.join(' '));
    }
}
